<script setup>
import { TooltipContent, TooltipPortal, TooltipRoot, TooltipTrigger } from "reka-ui";
import { ref, useSlots, useTemplateRef, watch } from "vue";

const props = defineProps({
  text: { type: String, default: undefined },
  position: { type: String, default: "top" },
  onlyShowOnOverflow: { type: Boolean, default: false },
  openOnClick: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const open = ref(false);
const trigger = useTemplateRef("trigger");
const slots = useSlots();
let openedByClick = false;

watch(
  () => props.disabled || !hasContent(),
  (closed) => {
    if (closed) open.value = false;
  },
);

function setOpen(value) {
  open.value =
    value &&
    !props.disabled &&
    hasContent() &&
    (!props.onlyShowOnOverflow || isOverflowing(trigger.value.$el));
  openedByClick = false;
}

function toggleOnClick() {
  const close = open.value && openedByClick;
  setOpen(!close);
  openedByClick = !close;
}

function hasContent() {
  return !!(props.text || slots.content);
}

function isOverflowing(element) {
  return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
}
</script>

<template>
  <TooltipRoot :open :disabled :disable-closing-trigger="openOnClick" @update:open="setOpen">
    <TooltipTrigger ref="trigger" as-child @click="openOnClick && toggleOnClick()">
      <slot />
    </TooltipTrigger>
    <TooltipPortal v-if="hasContent()">
      <TooltipContent class="base-tooltip-content" :side="position" :side-offset="10">
        <slot name="content">{{ text }}</slot>
      </TooltipContent>
    </TooltipPortal>
  </TooltipRoot>
</template>

<style lang="scss">
@use "@/colors" as c;
@use "@/styles/layers";
@use "@/styles/mixins/typography";

.base-tooltip-content {
  @include typography.small;

  z-index: layers.$floating;
  max-width: 360px;
  padding: 6px 10px;
  background: c.$main-hover;
  color: c.$text-inverse;
  border-radius: 4px;

  a {
    color: c.$tooltip-link;
    text-decoration: underline;
  }
}
</style>
